<template>
  <div class="mention-text">
    <template v-for="(seg, i) in segments" :key="i">
      <span
        v-if="seg.type === 'mention' && isPendingLocationMention(seg)"
        data-testid="location-mention"
        :data-location-id="seg.id"
        data-saving="true"
        class="group/mention cursor-default"
        :class="mentionLinkClass('location', false)"
        title="Locatie wordt opgeslagen…"
      >
        {{ getPendingLocationBaseLabel(seg.id) }}
        <FontAwesomeIcon
          :icon="faSpinner"
          class="ml-1 hidden size-3 animate-spin align-middle group-hover/mention:inline-block motion-reduce:animate-none"
          aria-hidden="true"
        />
      </span>
      <component
        v-else-if="seg.type === 'mention'"
        :is="enableLinks ? 'a' : 'span'"
        :data-testid="mentionTestId(seg.kind)"
        :data-location-id="seg.kind === 'location' ? seg.id : undefined"
        :data-file-id="seg.kind === 'file' ? seg.id : undefined"
        :data-person-id="seg.kind === 'person' ? seg.id : undefined"
        :data-project-id="seg.kind === 'project' ? seg.id : undefined"
        :data-business-id="seg.kind === 'business' ? seg.id : undefined"
        :role="enableLinks ? 'button' : undefined"
        :tabindex="enableLinks ? 0 : undefined"
        :class="mentionLinkClass(seg.kind, enableLinks)"
        :title="mentionTitle(seg.kind, seg.id, seg.label)"
        @click="onMentionClick(seg, $event)"
        @dblclick="onMentionDblClick(seg, $event)"
        @keydown.enter="onMentionClick(seg, $event)"
      >
        {{ displayLabel(seg.kind, seg.id, seg.label) }}
      </component>
      <span v-else-if="plainText">{{ seg.value }}</span>
      <span v-else v-html="renderText(seg.value)" />
    </template>

    <LocationMapModal
      v-if="mapLocation"
      :inline="usesInlineLocationMap()"
      :location="mapLocation"
      @saved="onLocationSaved"
      @close="closeMap"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, watch } from "vue"
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome"
import { faSpinner } from "@fortawesome/free-solid-svg-icons"
import LocationMapModal from "@/components/mention/LocationMapModal.vue"
import { convertLinks } from "@/helper/message-helper"
import { Bus } from "@/services/bus"
import {
  useMention,
  getPendingLocationBaseLabel,
  isPendingLocationId,
  mentionTestId
} from "@/composables/mention/use-mention"

const props = withDefaults(
  defineProps<{
    text?: string | null
    enableLinks?: boolean
    /** Render source text literally while still resolving Peanuts mentions. */
    plainText?: boolean
    preferLocationAddress?: boolean
    locationNameWithAddress?: boolean
    useLocationDetailModal?: boolean
  }>(),
  {
    text: "",
    enableLinks: true,
    plainText: false,
    preferLocationAddress: false,
    locationNameWithAddress: false,
    useLocationDetailModal: false
  }
)

const {
  parseBody,
  mentionLinkClass,
  labelFor,
  mentionTitle,
  ensureLoaded,
  mapLocation,
  openMention,
  handleLocationMentionClick,
  handleLocationMentionDoubleClick,
  closeMap,
  refreshOpenMapLocation,
  usesInlineLocationMap,
  locationLabelsRevision
} = useMention({
  preferLocationAddress: props.preferLocationAddress,
  locationNameWithAddress: props.locationNameWithAddress,
  useLocationDetailModal: props.useLocationDetailModal
})

const segments = computed(() => parseBody(props.text ?? ""))

function displayLabel(kind: Parameters<typeof labelFor>[0], id: string, storedLabel?: string) {
  void locationLabelsRevision.value
  return labelFor(kind, id, storedLabel)
}

function onLocationSaved() {
  closeMap()
}

function onInstructionTextChanged() {
  void refreshOpenMapLocation()
}

onMounted(() => Bus.on("instruction-text-changed", onInstructionTextChanged))
onBeforeUnmount(() => Bus.off("instruction-text-changed", onInstructionTextChanged))

watch(
  segments,
  (segs) =>
    segs.forEach((s) => s.type === "mention" && s.kind === "location" && ensureLoaded(s.id)),
  { immediate: true }
)

function isPendingLocationMention(seg: { type: string; kind?: string; id?: string }) {
  return seg.type === "mention" && seg.kind === "location" && isPendingLocationId(seg.id!)
}

function renderText(value: string): string {
  return props.enableLinks ? convertLinks(value) : value
}

function onMentionClick(
  seg: { kind: "location" | "file" | "person" | "project" | "business"; id: string },
  event: Event
) {
  if (!props.enableLinks) return
  event.stopPropagation()
  event.preventDefault()
  if (seg.kind === "location") {
    handleLocationMentionClick(seg.id, event)
    return
  }
  openMention(seg.kind, seg.id, event)
}

function onMentionDblClick(
  seg: { kind: "location" | "file" | "person" | "project" | "business"; id: string },
  event: Event
) {
  if (!props.enableLinks || seg.kind !== "location") return
  handleLocationMentionDoubleClick(seg.id, event)
}
</script>
